import {observer} from 'mobx-react';
import {DISTANCE_CALC_TYPES} from '../../../lib/types';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {Props as NOProps} from '../no-filter';
import FilterContainer from '../filter-container';

type Props = {
    showRosterSelection?: boolean;
};

const NetworkAnalysisGeographyFilter = observer((props: Props) => {
    const {showRosterSelection = false} = props;
    const noStore = useContext(NetworkContext);

    useEffect(() => {
        if (noStore.runId &&
            noStore.availableGeographyFilters.length === 0 &&
            !noStore.loadingGeographyData) {
            noStore.loadGeographyData();
        }
    }, [noStore.runId]);

    const distanceCalcTypeFilter: NOProps = useMemo(() => {
        const onChange = (e: string) => {
            noStore.distanceCalcType = e;
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item.value,
            (item: any): ReactNode => item.name,
        ];
        return {
            label: 'Distance Calculation Type',
            data: DISTANCE_CALC_TYPES,
            value: noStore.distanceCalcType,
            required: true,
            onChange,
            children,
            ['data-testid']: 'distance-calc-type-dropdown',
        };
    }, [noStore.runId, noStore.distanceCalcType]);

    const adequacyStandardFilter: NOProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'standard_type');
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_type', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Adequacy Standard',
            data,
            value: noStore.appliedGeographyFilter.standard_type,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'adequacy-standard-dropdown',
        };
    }, [noStore.runId, noStore.loadingGeographyData, distanceCalcTypeFilter, noStore.appliedGeographyFilter.standard_type]);

    const standardVersionFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'standard_version',
            geoFilterArgs,
            '',
            'DESC',
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_version', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Standard Version',
            data,
            value: noStore.appliedGeographyFilter.standard_version,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'standard-version-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        noStore.appliedGeographyFilter.standard_version
    ]);

    const patientRosterFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedGeographyFilter.patient_roster_name,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        adequacyStandardFilter,
        standardVersionFilter,
        noStore.appliedGeographyFilter.patient_roster_name
    ]);

    const stateFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'state_additional_info.state',
            geoFilterArgs
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('state', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'State',
            data,
            value: noStore.appliedGeographyFilter.state,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'state-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.state,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
    ]);

    const countyFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            'state_additional_info.state': noStore.appliedGeographyFilter.state,
            patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
            standard_version: noStore.appliedGeographyFilter.standard_version,
            standard_type: noStore.appliedGeographyFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('county', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        const disabled =
            !noStore.appliedGeographyFilter.state ||
            !noStore.appliedGeographyFilter.patient_roster_name;
        return {
            label: 'County',
            data,
            value: noStore.appliedGeographyFilter.county,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            disabled,
            ['data-testid']: 'county-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.county,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.patient_roster_name,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
        stateFilter,
    ]);

    const specialtyFilter: NOProps = useMemo(() => {
        const source =
            noStore.defaultComplianceScores.length > 0
                ? noStore.defaultComplianceScores
                : noStore.complianceScores;
        const data = source.map((score) => ({
            name: score.name,
            compliance: noStore.defaultComplianceScores.length > 0 ? '' : score.compliance,
        }));
        const onChange = (e: string) => {
            noStore.setSpecialtyFilter(e);
        };
        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any): string => item.name,
            (item: any): ReactNode => item.name || 'All',
        ];
        const disabled = !noStore.appliedGeographyFilter.standard_type;
        return {
            label: 'Specialty',
            data,
            value: noStore.specialtyFilter,
            onChange,
            children,
            spinner: noStore.loadingSpecialtyData,
            disabled,
            itemToString: (item: any) => item.name,
            ['data-testid']: 'specialty-dropdown',
        };
    }, [
        noStore.runId,
        noStore.defaultComplianceScores,
        noStore.complianceScores,
        noStore.specialtyFilter,
        noStore.loadingSpecialtyData,
        noStore.appliedGeographyFilter.standard_type,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
        stateFilter,
        countyFilter,
    ]);

    useEffect(() => {
        if (!noStore.appliedGeographyFilter.standard_type) {
            noStore.setSpecialtyFilter('');
        }
    }, [noStore.appliedGeographyFilter.standard_type]);

    const filterConfiguration: Array<NOProps> = [
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        specialtyFilter,
    ];

    return (
        <FilterContainer
            showRosterSelection={showRosterSelection}
            filterConfiguration={filterConfiguration}
        />
    );
});

export default NetworkAnalysisGeographyFilter;